import { ChangeEvent, Component, DragEvent } from 'react'
import { HiRefresh, HiUpload } from 'react-icons/hi'
import { toast } from 'react-toastify'
import { appRef } from '../../app'
import { ClientModel } from '../../api/clients'
import { deleteUsbFlashManifest, IUsbFlashManifestSummary, IUsbFlashManifestV1, listUsbFlashManifests, uploadUsbFlashArtifact } from '../../api/usb-flash-artifacts'
import { getString } from '../../locale'

type UploadModel = ClientModel.FILTER_FILLER | ClientModel.LTSP101 | ClientModel.LTSP70 | ClientModel.LTSP175 | ClientModel.LTCB220 | ClientModel.LTCB226
type UploadChip = 'esp32s3' | 'esp32p4' | 'esp32c6'
type FilterModel = UploadModel | 'all'

type BinEntry = {
    id: string
    file: File
    address: string
    path: string
}

type State = {
    loading: boolean
    uploading: boolean
    manifests: IUsbFlashManifestSummary[]
    manifestFile?: File
    parsedManifest?: IUsbFlashManifestV1
    binEntries: BinEntry[]
    id: string
    title: string
    model: UploadModel | ''
    chip: UploadChip | ''
    version: string
    overwrite: boolean
    filterQuery: string
    filterModel: FilterModel
    deletingId?: string
}

const uploadModelOptions: { model: UploadModel; label: () => string }[] = [
    { model: ClientModel.LTCB220, label: () => getString('client_model.ltcb220') },
    { model: ClientModel.LTCB226, label: () => getString('client_model.ltcb226') },
    { model: ClientModel.LTSP70, label: () => getString('client_model.ltsp70') },
    { model: ClientModel.LTSP101, label: () => getString('client_model.ltsp101') },
    { model: ClientModel.LTSP175, label: () => getString('client_model.ltsp175') },
    { model: ClientModel.FILTER_FILLER, label: () => getString('client_model.filter_filler') },
]

function chipByModel(model: UploadModel | ''): 'esp32s3' | 'esp32p4' | '' {
    if (model === ClientModel.LTSP101 || model === ClientModel.LTSP70 || model === ClientModel.LTSP175) return 'esp32p4'
    if (model === ClientModel.LTCB220 || model === ClientModel.LTCB226 || model === ClientModel.FILTER_FILLER) return 'esp32s3'
    return ''
}

const uploadChipOptions: UploadChip[] = ['esp32s3', 'esp32p4', 'esp32c6']

function modelLabel(model?: number): string {
    if (model === ClientModel.LTCB220) return getString('client_model.ltcb220')
    if (model === ClientModel.LTCB226) return getString('client_model.ltcb226')
    if (model === ClientModel.LTSP70) return getString('client_model.ltsp70')
    if (model === ClientModel.LTSP101) return getString('client_model.ltsp101')
    if (model === ClientModel.LTSP175) return getString('client_model.ltsp175')
    if (model === ClientModel.FILTER_FILLER) return getString('client_model.filter_filler')
    return ''
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const raw = String(reader.result ?? '')
            const commaIndex = raw.indexOf(',')
            resolve(commaIndex >= 0 ? raw.slice(commaIndex + 1) : raw)
        }
        reader.onerror = () => reject(new Error('Failed reading file'))
        reader.readAsDataURL(file)
    })
}

function binEntryId(file: File): string {
    return `${file.name.toLowerCase()}-${file.size}-${file.lastModified}`
}

function normalizeAddress(raw: string): string {
    let value = raw.trim().toLowerCase()
    if (value.length === 0) return value
    if (!value.startsWith('0x')) value = `0x${value}`
    return value
}

function isValidAddress(raw: string): boolean {
    return /^0x[0-9a-f]+$/i.test(raw.trim())
}

function baseName(pathLike: string): string {
    const clean = String(pathLike || '').trim().toLowerCase()
    const last = clean.split('/').pop() || clean
    return last
}

function normalizeNameForMatch(pathLike: string): string {
    const name = baseName(pathLike).replace(/\.bin$/i, '')
    return name.replace(/[^a-z0-9]+/g, ' ').trim()
}

function manifestCandidates(manifest: IUsbFlashManifestV1): Array<{ address: string; path: string; norm: string }> {
    return Object.entries(manifest.flash_files ?? {}).map(([address, relPathRaw]) => {
        const path = String(relPathRaw ?? '').trim()
        return {
            address: normalizeAddress(address),
            path,
            norm: normalizeNameForMatch(path),
        }
    })
}

function extractVersionFromFileName(fileName: string): string | undefined {
    const lower = fileName.toLowerCase()
    const noExt = lower.replace(/\.bin$/i, '')
    const match = noExt.match(/(?:^|[^a-z0-9])v?(\d+\.\d+\.\d+(?:[-._]?[a-z0-9]+)*)/i)
    return match?.[1]
}

function detectVersionFromBins(entries: BinEntry[]): string | undefined {
    const appFirst = [...entries].sort((a, b) => {
        const aApp = a.file.name.toLowerCase().includes('app') ? 0 : 1
        const bApp = b.file.name.toLowerCase().includes('app') ? 0 : 1
        return aApp - bApp
    })
    for (const entry of appFirst) {
        const version = extractVersionFromFileName(entry.file.name)
        if (version) return version
    }
    return undefined
}

export class UsbFlashArtifactsPage extends Component<{}, State> {
    state: State = {
        loading: true,
        uploading: false,
        manifests: [],
        binEntries: [],
        id: '',
        title: '',
        model: '',
        chip: '',
        version: '',
        overwrite: false,
        filterQuery: '',
        filterModel: 'all',
    }

    componentDidMount = async () => {
        appRef.setHeader(<HiUpload className="me-2" size={28} />, () => getString('pagelabels.usb_flash_artifacts'))
        await this.load()
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            const response = await listUsbFlashManifests()
            this.setState({ manifests: response.data })
        } catch (error: any) {
            toast.error(error?.message || getString('usb_flash_artifacts.load_failed'))
        } finally {
            this.setState({ loading: false })
        }
    }

    private manifestBaseName(item: IUsbFlashManifestSummary): string {
        const byModel = modelLabel(item.model)
        if (byModel) return byModel
        const title = String(item.title || '').trim()
        if (title) return title
        return item.id
    }

    private manifestDisplayName(item: IUsbFlashManifestSummary): string {
        const base = this.manifestBaseName(item)
        const version = String(item.version || '').trim()
        return version ? `${base} + ${version}` : base
    }

    private deleteManifest = async (item: IUsbFlashManifestSummary) => {
        if (!window.confirm(getString('usb_flash_artifacts.delete_confirm', item.id))) return
        this.setState({ deletingId: item.id })
        try {
            await deleteUsbFlashManifest(item.id)
            toast.success(getString('usb_flash_artifacts.delete_success'))
            await this.load()
        } catch (error: any) {
            toast.error(error?.message || getString('usb_flash_artifacts.delete_failed'))
        } finally {
            this.setState({ deletingId: undefined })
        }
    }

    private onManifestDrop = async (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault()
        const file = event.dataTransfer.files?.[0]
        if (!file) return
        await this.parseManifestFile(file)
    }

    private onManifestPick = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        await this.parseManifestFile(file)
    }

    private async parseManifestFile(file: File) {
        try {
            const rawText = await file.text()
            const parsed = JSON.parse(rawText) as IUsbFlashManifestV1
            if (!parsed.flash_files || typeof parsed.flash_files !== 'object') {
                throw new Error('manifest.flash_files is required')
            }
            if (Object.keys(parsed.flash_files).length === 0) {
                throw new Error('manifest.flash_files is empty')
            }

            const updatedEntries = this.autoAssignByManifest(this.state.binEntries, parsed)
            this.setState({
                manifestFile: file,
                parsedManifest: parsed,
                binEntries: updatedEntries,
            })
            toast.success(getString('usb_flash_artifacts.manifest_parsed'))
        } catch (error: any) {
            toast.error(`${getString('usb_flash_artifacts.manifest_parse_failed')}: ${error?.message || error}`)
        }
    }

    private onBinFilesPick = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])
        this.addBinFiles(files)
    }

    private onBinFilesDrop = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault()
        const files = Array.from(event.dataTransfer.files ?? [])
        this.addBinFiles(files)
    }

    private addBinFiles(files: File[]) {
        const bins = files.filter(file => file.name.toLowerCase().endsWith('.bin'))
        if (bins.length === 0) {
            toast.error('Drop BIN files only')
            return
        }

        const map = new Map<string, BinEntry>()
        for (const item of this.state.binEntries) map.set(item.id, item)

        for (const file of bins) {
            const id = binEntryId(file)
            const existing = map.get(id)
            map.set(id, {
                id,
                file,
                address: existing?.address ?? '',
                path: existing?.path ?? file.name,
            })
        }

        const entries = Array.from(map.values())
        const autoVersion = this.state.version.trim() || detectVersionFromBins(entries) || ''
        this.setState({
            binEntries: this.state.parsedManifest ? this.autoAssignByManifest(entries, this.state.parsedManifest) : entries,
            version: autoVersion,
        })
    }

    private autoAssignByManifest(entries: BinEntry[], manifest: IUsbFlashManifestV1): BinEntry[] {
        const candidates = manifestCandidates(manifest)
        const used = new Set<string>()
        const mapped = entries.map(entry => ({ ...entry }))

        const tryAssign = (entryIndex: number, candidate: { address: string; path: string }) => {
            if (used.has(candidate.path)) return false
            mapped[entryIndex].address = candidate.address
            mapped[entryIndex].path = candidate.path
            used.add(candidate.path)
            return true
        }

        // Pass 1: exact basename/path match.
        mapped.forEach((entry, index) => {
            const entryPath = entry.path.toLowerCase()
            const entryBase = baseName(entry.file.name)
            const exact = candidates.find(item => item.path.toLowerCase() === entryPath || baseName(item.path) === entryBase)
            if (exact) tryAssign(index, exact)
        })

        // Pass 2: fuzzy normalized-name match (handles versioned names like app-3.1.8.bin -> app.bin).
        mapped.forEach((entry, index) => {
            if (entry.path && used.has(entry.path)) return
            const entryNorm = normalizeNameForMatch(entry.file.name)
            if (!entryNorm) return
            const matches = candidates.filter(item => {
                if (used.has(item.path)) return false
                if (!item.norm) return false
                return entryNorm === item.norm || entryNorm.startsWith(item.norm) || item.norm.startsWith(entryNorm)
            })
            if (matches.length === 1) {
                tryAssign(index, matches[0])
            }
        })

        return mapped
    }

    private removeBin(id: string) {
        this.setState({
            binEntries: this.state.binEntries.filter(entry => entry.id !== id),
        })
    }

    private updateBinAddress(id: string, rawAddress: string) {
        this.setState({
            binEntries: this.state.binEntries.map(entry => (entry.id === id ? { ...entry, address: rawAddress } : entry)),
        })
    }

    upload = async () => {
        const { parsedManifest, binEntries } = this.state
        const selectedChip = this.state.chip || chipByModel(this.state.model)
        if (!selectedChip) {
            toast.error('Select chip before upload')
            return
        }
        if (binEntries.length === 0) {
            toast.error('Add at least one BIN file')
            return
        }

        this.setState({ uploading: true })
        try {
            let manifest: IUsbFlashManifestV1
            const files: Record<string, string> = {}

            if (parsedManifest) {
                manifest = parsedManifest
                manifest = {
                    ...manifest,
                    extra_esptool_args: {
                        ...(manifest.extra_esptool_args ?? {}),
                        chip: selectedChip,
                    },
                }
                const remaining = [...binEntries]
                const popMatch = (predicate: (entry: BinEntry) => boolean): BinEntry | undefined => {
                    const idx = remaining.findIndex(predicate)
                    if (idx < 0) return undefined
                    const [entry] = remaining.splice(idx, 1)
                    return entry
                }

                for (const [addressRaw, relPathRaw] of Object.entries(parsedManifest.flash_files ?? {})) {
                    const relPath = String(relPathRaw ?? '').trim()
                    const address = normalizeAddress(addressRaw)
                    const relBase = baseName(relPath)
                    const relNorm = normalizeNameForMatch(relPath)
                    const entry =
                        popMatch(item => item.path === relPath) ||
                        popMatch(item => baseName(item.file.name) === relBase) ||
                        popMatch(item => {
                            const itemNorm = normalizeNameForMatch(item.file.name)
                            return !!itemNorm && !!relNorm && (itemNorm === relNorm || itemNorm.startsWith(relNorm) || relNorm.startsWith(itemNorm))
                        }) ||
                        popMatch(item => normalizeAddress(item.address) === address)

                    if (!entry) {
                        toast.error(getString('usb_flash_artifacts.missing_segment', relPath))
                        this.setState({ uploading: false })
                        return
                    }
                    files[relPath] = await fileToBase64(entry.file)
                }
            } else {
                const flashFiles: Record<string, string> = {}
                const usedAddresses = new Set<string>()
                for (const entry of binEntries) {
                    const address = normalizeAddress(entry.address)
                    if (!isValidAddress(address)) {
                        toast.error(`Invalid address for ${entry.file.name}: ${entry.address || '-'}`)
                        this.setState({ uploading: false })
                        return
                    }
                    if (usedAddresses.has(address)) {
                        toast.error(`Duplicate address: ${address}`)
                        this.setState({ uploading: false })
                        return
                    }
                    usedAddresses.add(address)
                    const path = entry.file.name
                    flashFiles[address] = path
                    files[path] = await fileToBase64(entry.file)
                }

                manifest = {
                    write_flash_args: ['--flash_mode', 'dio', '--flash_size', 'detect', '--flash_freq', '80m'],
                    flash_settings: {
                        flash_mode: 'dio',
                        flash_size: 'detect',
                        flash_freq: '80m',
                    },
                    flash_files: flashFiles,
                    extra_esptool_args: {
                        after: 'hard_reset',
                        before: 'default_reset',
                        stub: true,
                        chip: selectedChip,
                    },
                }
            }

            await uploadUsbFlashArtifact({
                id: this.state.id.trim() || undefined,
                title: this.state.title.trim() || undefined,
                model: this.state.model === '' ? undefined : this.state.model,
                chip: selectedChip,
                version: this.state.version.trim() || undefined,
                overwrite: this.state.overwrite,
                manifest,
                files,
            })

            toast.success(getString('usb_flash_artifacts.upload_success'))
            this.setState({
                manifestFile: undefined,
                parsedManifest: undefined,
                binEntries: [],
                id: '',
                title: '',
                model: '',
                chip: '',
                version: '',
                overwrite: false,
            })
            await this.load()
        } catch (error: any) {
            toast.error(error?.message || getString('usb_flash_artifacts.upload_failed'))
        } finally {
            this.setState({ uploading: false })
        }
    }

    render() {
        const { loading, manifests, filterModel, filterQuery, deletingId } = this.state
        const normalizedQuery = filterQuery.trim().toLowerCase()
        const filteredManifests = manifests.filter(item => {
            if (filterModel !== 'all' && item.model !== filterModel) return false
            if (!normalizedQuery) return true
            const haystack = `${this.manifestBaseName(item)} ${item.title || ''} ${item.id}`.toLowerCase()
            return haystack.includes(normalizedQuery)
        })
        return (
            <div className="space-y-6">
                <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6">
                    <h2 className="text-xl font-semibold dark:text-white mb-1">{getString('usb_flash_artifacts.title')}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{getString('usb_flash_artifacts.desc')}</p>
                </div>

                <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6 space-y-4">
                    <h3 className="text-lg font-semibold dark:text-white">{getString('usb_flash_artifacts.upload_title')}</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input
                            value={this.state.id}
                            onChange={e => this.setState({ id: e.target.value })}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            placeholder={getString('usb_flash_artifacts.id_placeholder')}
                        />
                        <input
                            value={this.state.title}
                            onChange={e => this.setState({ title: e.target.value })}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            placeholder={getString('usb_flash_artifacts.title_placeholder')}
                        />
                        <select
                            value={this.state.model}
                            onChange={e => {
                                const model = e.target.value === '' ? '' : (Number(e.target.value) as UploadModel)
                                this.setState({
                                    model,
                                    chip: this.state.chip || chipByModel(model),
                                })
                            }}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                        >
                            <option value="">{getString('usb_flash_artifacts.model_placeholder')}</option>
                            {uploadModelOptions.map(option => (
                                <option key={option.model} value={option.model}>
                                    {option.label()}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <select
                            value={this.state.chip}
                            onChange={e => this.setState({ chip: e.target.value === '' ? '' : (e.target.value as UploadChip) })}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                        >
                            <option value="">{getString('usb_flash_artifacts.chip_placeholder')}</option>
                            {uploadChipOptions.map(chip => (
                                <option key={chip} value={chip}>
                                    {chip}
                                </option>
                            ))}
                        </select>
                        <input
                            value={this.state.version}
                            onChange={e => this.setState({ version: e.target.value })}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            placeholder={getString('usb_flash_artifacts.version_placeholder')}
                        />
                        <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300 px-1">
                            <input type="checkbox" checked={this.state.overwrite} onChange={e => this.setState({ overwrite: e.target.checked })} />
                            {getString('usb_flash_artifacts.overwrite')}
                        </label>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <label
                            onDragOver={event => event.preventDefault()}
                            onDrop={this.onManifestDrop}
                            className="rounded border-2 border-dashed border-gray-300 dark:border-gray-600 p-4 cursor-pointer bg-gray-50 dark:bg-gray-900"
                        >
                            <div className="font-semibold text-gray-800 dark:text-gray-100">{getString('usb_flash_artifacts.manifest_json')}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Drag & drop `.json` here or click to choose</div>
                            <input type="file" accept=".json,application/json" onChange={this.onManifestPick} className="hidden" />
                            <div className="text-xs text-green-700 dark:text-green-300 mt-2">{this.state.manifestFile?.name || '-'}</div>
                        </label>

                        <label
                            onDragOver={event => event.preventDefault()}
                            onDrop={this.onBinFilesDrop}
                            className="rounded border-2 border-dashed border-gray-300 dark:border-gray-600 p-4 cursor-pointer bg-gray-50 dark:bg-gray-900"
                        >
                            <div className="font-semibold text-gray-800 dark:text-gray-100">{getString('usb_flash_artifacts.segments')}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Drag & drop `.bin` files here or click to choose multiple</div>
                            <input type="file" multiple accept=".bin,application/octet-stream" onChange={this.onBinFilesPick} className="hidden" />
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">{this.state.binEntries.length} BIN file(s)</div>
                        </label>
                    </div>

                    {this.renderBinTable()}

                    <button
                        onClick={this.upload}
                        disabled={this.state.uploading || (this.state.chip === '' && chipByModel(this.state.model) === '') || this.state.binEntries.length === 0}
                        className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                        {this.state.uploading ? getString('common.loading') : getString('usb_flash_artifacts.upload_button')}
                    </button>
                </div>

                <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold dark:text-white">{getString('usb_flash_artifacts.existing')}</h3>
                        <button onClick={this.load} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2">
                            <HiRefresh /> {getString('license.refresh')}
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <input
                            value={filterQuery}
                            onChange={event => this.setState({ filterQuery: event.target.value })}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            placeholder={getString('usb_flash_artifacts.filter_title_placeholder')}
                        />
                        <select
                            value={filterModel}
                            onChange={event => {
                                const value = event.target.value
                                this.setState({
                                    filterModel: value === 'all' ? 'all' : (Number(value) as UploadModel),
                                })
                            }}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                        >
                            <option value="all">{getString('usb_flash_artifacts.filter_model_all')}</option>
                            {uploadModelOptions.map(option => (
                                <option key={option.model} value={option.model}>
                                    {option.label()}
                                </option>
                            ))}
                        </select>
                    </div>
                    {loading ? (
                        <div className="text-gray-600 dark:text-gray-300">{getString('common.loading')}...</div>
                    ) : filteredManifests.length === 0 ? (
                        <div className="text-gray-600 dark:text-gray-300">{getString('usb_flash_artifacts.empty')}</div>
                    ) : (
                        <div className="space-y-2">
                            {filteredManifests.map(item => (
                                <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="font-semibold text-gray-900 dark:text-white">{this.manifestDisplayName(item)}</div>
                                        <button
                                            onClick={() => this.deleteManifest(item)}
                                            disabled={deletingId === item.id}
                                            className="rounded bg-red-600 text-white px-3 py-1 text-xs hover:bg-red-700 disabled:opacity-60"
                                        >
                                            {deletingId === item.id ? getString('common.loading') : getString('common.delete')}
                                        </button>
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300">
                                        id={item.id} · chip={item.chip || '-'} · model={item.model ?? '-'} · version={item.version || '-'}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">updated: {new Date(item.updatedAt).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    private renderBinTable() {
        const { binEntries, parsedManifest } = this.state
        if (binEntries.length === 0) return undefined

        return (
            <div className="space-y-2">
                <h4 className="text-md font-semibold dark:text-white">BIN mapping</h4>
                {binEntries.map(entry => (
                    <div key={entry.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-gray-200 dark:border-gray-700 rounded p-3">
                        <div className="md:col-span-4 text-sm text-gray-800 dark:text-gray-200">{entry.file.name}</div>
                        <input
                            value={entry.address}
                            onChange={event => this.updateBinAddress(entry.id, event.target.value)}
                            className="md:col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            placeholder="Address (e.g. 0x230000)"
                        />
                        <div className="md:col-span-4 text-xs text-gray-600 dark:text-gray-400 truncate">
                            {parsedManifest ? entry.path : entry.file.name}
                        </div>
                        <button
                            onClick={() => this.removeBin(entry.id)}
                            className="md:col-span-1 rounded bg-red-600 text-white px-2 py-2 text-xs hover:bg-red-700"
                        >
                            X
                        </button>
                    </div>
                ))}
            </div>
        )
    }
}
